import React, { VFC, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/css';
import formBg from '../assets/images/form-bg.png';

type FormModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  name: string;
  contact: string;
  description: string;
  budget: string;
  timeline: string;
};

export const FormModal: VFC<FormModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    contact: '',
    description: '',
    budget: '',
    timeline: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    // 关闭时清除验证错误和状态
    setErrors({});
    setIsSuccess(false);
    setCountdown(5);
    setIsLoading(false);
    onClose();
  };

  // 自动关闭倒计时
  useEffect(() => {
    if (isOpen && isSuccess) {
      if (countdown <= 0) {
        handleClose();
        return;
      }
      
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, isSuccess, countdown]);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 清除该字段的错误
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = '请输入您的称呼';
    }
    if (!formData.contact.trim()) {
      newErrors.contact = '请输入联系方式';
    }
    if (!formData.description.trim()) {
      newErrors.description = '请描述您的需求';
    }
    if (!formData.budget.trim()) {
      newErrors.budget = '请输入预算范围';
    }
    if (!formData.timeline) {
      newErrors.timeline = '请选择期望完成时间';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate()) {
      setIsLoading(true);
      
      try {
        // 获取 API 基础地址
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        
        // 调用后端 API 提交数据
        const response = await fetch(`${API_BASE_URL}/api/submit-form`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const result = await response.json();

        if (result.success) {
          console.log('表单提交成功:', result);
          
          // 显示成功状态
          setIsSuccess(true);
          
          // 重置表单
          setFormData({
            name: '',
            contact: '',
            description: '',
            budget: '',
            timeline: '',
          });
        } else {
          console.error('表单提交失败:', result.message);
          alert(`提交失败: ${result.message}`);
        }
      } catch (error) {
        console.error('网络错误:', error);
        alert('网络错误，请检查后端服务是否启动');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        <button 
          className={styles.closeBtn} 
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }} 
          aria-label="关闭"
        >
          ×
        </button>
        
        <div className={styles.content}>
          {isSuccess ? (
            <div className={styles.successContent}>
              <div className={styles.successIconWrapper}>
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="32" cy="32" r="30" stroke="#121619" strokeWidth="4"/>
                  <path d="M20 32L28 40L44 24" stroke="#121619" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p className={styles.successText}>感谢您的填写，我们将尽快与您联系。</p>
              <p className={styles.countdownText}>{countdown}s后将自动关闭此窗口</p>
              <button className={styles.knowBtn} onClick={handleClose}>
                我知道了
              </button>
            </div>
          ) : (
            <div className={styles.formSection}>
              <h2 className={styles.formTitle}>需求表单</h2>
              
              <form method="post" onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>您的称呼</label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="请填写称呼"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                  />
                  {errors.name && <span className={styles.error}>{errors.name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>联系方式（手机/微信号）</label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="请填写联系方式"
                    value={formData.contact}
                    onChange={(e) => handleChange('contact', e.target.value)}
                  />
                  {errors.contact && <span className={styles.error}>{errors.contact}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>一句话描述您想做什么？</label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="例如：定制一个公司官网/设计一套品牌Logo"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                  />
                  {errors.description && <span className={styles.error}>{errors.description}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>您的预算范围是？</label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="请填写预算范围"
                    value={formData.budget}
                    onChange={(e) => handleChange('budget', e.target.value)}
                  />
                  {errors.budget && <span className={styles.error}>{errors.budget}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>期望何时完成？</label>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="timeline"
                        value="1个月内"
                        checked={formData.timeline === '1个月内'}
                        onChange={(e) => handleChange('timeline', e.target.value)}
                      />
                      <span>1个月内</span>
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="timeline"
                        value="3个月"
                        checked={formData.timeline === '3个月'}
                        onChange={(e) => handleChange('timeline', e.target.value)}
                      />
                      <span>3个月</span>
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="timeline"
                        value="半年内"
                        checked={formData.timeline === '半年内'}
                        onChange={(e) => handleChange('timeline', e.target.value)}
                      />
                      <span>半年内</span>
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="timeline"
                        value="时间灵活"
                        checked={formData.timeline === '时间灵活'}
                        onChange={(e) => handleChange('timeline', e.target.value)}
                      />
                      <span>时间灵活</span>
                    </label>
                  </div>
                  {errors.timeline && <span className={styles.error}>{errors.timeline}</span>}
                </div>

                <button type="submit" className={styles.submitBtn} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className={styles.spinner}></span>
                      提交中...
                    </>
                  ) : (
                    '提交'
                  )}
                </button>
              </form>
            </div>
          )}

          {/* 右侧：交付流程 - 始终显示 */}
          <div className={styles.processSection}>
            <h2 className={styles.processTitle}>交付流程</h2>
            
            <div className={styles.steps}>
              <div className={styles.step}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepContent}>
                  <h3 className={styles.stepTitle}>业务探索</h3>
                  <p className={styles.stepDesc}>深入理解业务，共同定义AI核心意图。</p>
                </div>
              </div>

              <div className={styles.step}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepContent}>
                  <h3 className={styles.stepTitle}>方案验证</h3>
                  <p className={styles.stepDesc}>设计专属方案，通过POC快速验证核心价值。</p>
                </div>
              </div>

              <div className={styles.step}>
                <div className={styles.stepNumber}>3</div>
                <div className={styles.stepContent}>
                  <h3 className={styles.stepTitle}>数据交付</h3>
                  <p className={styles.stepDesc}>数据开发，并完整集成至您现有的工作流。</p>
                </div>
              </div>

              <div className={styles.step}>
                <div className={styles.stepNumber}>4</div>
                <div className={styles.stepContent}>
                  <h3 className={styles.stepTitle}>持续赋能</h3>
                  <p className={styles.stepDesc}>交付上线，并为后续做伙伴持续迭代优化。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

const styles = {
  backdrop: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
    pointer-events: auto;
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
  `,
  modal: css`
    position: relative;
    width: 100%;
    max-width: 1100px;
    max-height: 90vh;
    overflow: hidden;
    border-radius: 8px;
    animation: slideUp 0.3s ease;

    @keyframes slideUp {
      from {
        transform: translateY(30px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
  `,
  closeBtn: css`
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    font-size: 24px;
    line-height: 1;
    border-radius: 30px;
    cursor: pointer;
    z-index: 10;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    &:hover {
      background: rgba(255, 255, 255, 0.25);
      transform: rotate(90deg);
    }

    @media (max-width: 768px) {
      color: #000;
      background: rgba(0, 0, 0, 0.05);
      top: 12px;
      right: 12px;
    }
  `,
  content: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 100%;
    max-height: 90vh;

    @media (max-width: 968px) {
      grid-template-columns: 1fr;
      overflow-y: auto;
    }
  `,
  formSection: css`
    background: #fff;
    padding: 40px;
    overflow-y: auto;

    @media (max-width: 768px) {
      padding: 30px 20px;
    }
  `,
  formTitle: css`
    font-size: 24px;
    font-weight: 700;
    color: #121619;
    margin: 0 0 30px 0;
  `,
  formGroup: css`
    margin-bottom: 24px;
  `,
  label: css`
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #121619;
    margin-bottom: 8px;
  `,
  input: css`
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 14px;
    color: #121619;
    transition: all 0.2s ease;
    font-family: inherit;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      outline: none;
      border-color: #514260;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
  `,
  radioGroup: css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-top: 8px;
  `,
  radioLabel: css`
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 14px;
    color: #4b5563;
    -webkit-tap-highlight-color: transparent;

    input[type='radio'] {
      width: 18px;
      height: 18px;
      margin-right: 8px;
      cursor: pointer;
      accent-color: #514260;
    }

    span {
      user-select: none;
    }
  `,
  error: css`
    display: block;
    color: #ef4444;
    font-size: 12px;
    margin-top: 6px;
  `,
  submitBtn: css`
    width: 100%;
    padding: 14px;
    background: #121619;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 8px;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:hover:not(:disabled) {
      background: #1f2937;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  `,
  spinner: css`
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top: 2px solid #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `,
  processSection: css`
    background: url(${formBg}) center/cover no-repeat;
    padding: 40px;
    color: #fff;
    overflow-y: auto;
    position: relative;

    & > * {
      position: relative;
      z-index: 1;
    }

    @media (max-width: 768px) {
      display: none;
    }
  `,
  processTitle: css`
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 30px 0;
  `,
  steps: css`
    display: flex;
    flex-direction: column;
    gap: 30px;
  `,
  step: css`
    background: rgba(255, 255, 255);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    gap: 16px;
    align-items: flex-start;
    transition: all 0.3s ease;
    color: #fff;

    &:hover {
      transform: translateX(4px);
    }
  `,
  stepNumber: css`
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #121619;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
  `,
  stepContent: css`
    flex: 1;
  `,
  stepTitle: css`
    font-size: 16px;
    font-weight: 700;
    color: #121619;
    margin: 0 0 6px 0;
  `,
  stepDesc: css`
    font-size: 13px;
    margin: 0;
    opacity: 0.9;
    line-height: 1.6;
    color: #697077;
  `,
  successContent: css`
    width: 100%;
    height: 100%;
    min-height: 500px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #fff;
    padding: 40px;
    text-align: center;
  `,
  successIconWrapper: css`
    margin-top: 80px;
    color: #121619;
  `,
  successText: css`
    font-size: 20px;
    color: #121619;
    margin-top: 15px;
    margin-bottom: 180px;
    font-weight: 600;
    letter-spacing: 1px;
  `,
  countdownText: css`
    font-size: 14px;
    color: #9ca3af;
    margin-bottom: 16px;
  `,
  knowBtn: css`
    width: 100%;
    max-width: 440px;
    padding: 14px 0;
    height: 50px;
    background: #121619;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;
    -webkit-tap-highlight-color: transparent;

    &:hover {
      background: #1f2937;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  `,
};
